import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { SubscriptionPrices } from '../_models/subscriptionPrices';
import { User } from '../_models';

type TrackingLogOptions = {
    firebaseName?: string;
    metaName?: string;
    value?: number;
    currency?: string;
    params?: { [key: string]: any };
};

interface TrackingPlugin {
    logEvent(options: TrackingLogOptions): Promise<void>;
    setUserProperty(options: { key: string; value: string | null }): Promise<void>;
    setAdvertiserTrackingEnabled(options: { enabled: boolean }): Promise<void>;
}

interface AttPlugin {
    requestTrackingAuthorization(): Promise<{ status: string }>;
    getTrackingAuthorizationStatus(): Promise<{ status: string }>;
}

const Tracking = registerPlugin<TrackingPlugin>('Tracking');
const ATT = registerPlugin<AttPlugin>('ATT');
const ATTLegacy = registerPlugin<AttPlugin>('ATTPlugin');

@Injectable({
    providedIn: 'root'
})
export class AdsTrackingService {
    private readonly vatRate = 0.2;
    private readonly debugEnabled = environment.debug === true;
    private readonly debugPrefix = '[TrackingDebug]';
    private readonly consentStoragePrefix = 'tracking_consent_granted';
    private readonly anonymousConsentScope = 'anon';
    private currentConsentScope = this.anonymousConsentScope;

    constructor(private http: HttpClient) {}

    async trackInstall(): Promise<void> {
        this.debug('trackInstall: called');
        await this.logNative({
            firebaseName: 'app_install',
            metaName: 'fb_mobile_install'
        });
    }

    async trackCompleteRegistration(eventId?: string, sendCapi: boolean = false): Promise<void> {
        this.debug('trackCompleteRegistration: called', { eventId, sendCapi });
        await this.logNative({
            firebaseName: 'sign_up',
            metaName: 'fb_mobile_complete_registration',
            params: eventId ? { event_id: eventId } : undefined
        });

        if (sendCapi) {
            await this.sendCapi('CompleteRegistration', undefined, undefined, eventId);
        }
    }

    async trackSignIn(method: string = 'password'): Promise<void> {
        if (!this.hasTrackingConsent()) {
            this.debug('trackSignIn: skipped (no consent)', { method });
            return;
        }

        this.debug('trackSignIn: called', { method });
        await this.logNative({
            firebaseName: 'sign_in',
            params: {
                method
            }
        });
    }

    async trackAccountDeleted(reason?: string | null): Promise<void> {
        if (!this.hasTrackingConsent()) {
            this.debug('trackAccountDeleted: skipped (no consent)', { reason });
            return;
        }

        this.debug('trackAccountDeleted: called', { reason });
        await this.logNative({
            firebaseName: 'account_deleted',
            params: reason ? { reason } : undefined
        });
    }

    async trackAccountPaused(reason?: string | null): Promise<void> {
        if (!this.hasTrackingConsent()) {
            this.debug('trackAccountPaused: skipped (no consent)', { reason });
            return;
        }

        this.debug('trackAccountPaused: called', { reason });
        await this.logNative({
            firebaseName: 'account_paused',
            params: reason ? { reason } : undefined
        });
    }

    async trackPurchase(value: number, currency: string, eventId?: string, sendCapi: boolean = true): Promise<void> {
        this.debug('trackPurchase: called', { value, currency, eventId, sendCapi });
        await this.logNative({
            firebaseName: 'purchase',
            metaName: 'fb_mobile_purchase',
            value,
            currency,
            params: eventId ? { event_id: eventId } : undefined
        });

        if (sendCapi) {
            await this.sendCapi('Purchase', value, currency, eventId);
        }
    }

    async trackSubscriptionActivated(
        type: 'geek' | 'branche' | 'vip' | 'vip_promo' | 'vip_pixel' | null | undefined,
        origin?: string | null
    ): Promise<void> {
        this.debug('trackSubscriptionActivated: called', { type, origin });
        await this.logNative({
            firebaseName: 'subscription_activated',
            params: {
                subscription_type: type ?? 'unknown',
                subscription_origin: origin ?? 'unknown'
            }
        });
    }

    async trackScreenView(url: string, user?: User | null): Promise<void> {
        if (!this.hasTrackingConsent()) {
            this.debug('trackScreenView: skipped (no consent)', { url });
            return;
        }

        const path = this.normalizePath(url);
        const screenName = this.mapScreenName(path);
        const genderUniverse = this.toGenderUniverse(user?.gender);
        const subscribed = this.toSubscriptionStatus(user);
        const metaScreenView = this.getMetaScreenViewConfig(path, screenName);
        this.debug('trackScreenView: sending', {
            url,
            path,
            screenName,
            genderUniverse,
            subscribed,
            metaScreenView
        });

        await this.logNative({
            firebaseName: 'screen_view',
            metaName: metaScreenView?.metaName,
            params: {
                screen_name: screenName,
                screen_class: 'AngularRoute',
                route_path: path,
                // gender_universe: genderUniverse,
                // subscription_status: subscribed,
                ...(metaScreenView?.params ?? {})
            }
        });
    }

    async trackCampaignFromUrl(url: string, user?: User | null): Promise<void> {
        console.log('trackCampaignFromUrl: called with url', url);
        if (!this.hasTrackingConsent()) {
            this.debug('trackCampaignFromUrl: skipped (no consent)', { url });
            return;
        }

        console.log('trackCampaignFromUrl: has consent, extracting params');

        const params = this.extractTrackingParams(url);
        if (!params) {
            this.debug('trackCampaignFromUrl: no tracking params', { url });
            return;
        }
        this.debug('trackCampaignFromUrl: extracted params', params);

        if (params.utmSource) {
            await this.setUserProperty('acq_source_last', params.utmSource);
        }

        if (params.utmCampaign) {
            await this.setUserProperty('acq_campaign_last', params.utmCampaign);
        }

        await this.logNative({
            firebaseName: 'campaign_landing',
            metaName: 'fb_mobile_content_view',
            params: {
                content_type: 'landing_page',
                content_id: params.utmCampaign ?? params.path,
                landing_path: params.path,
                utm_source: params.utmSource,
                utm_medium: params.utmMedium,
                utm_campaign: params.utmCampaign,
                utm_content: params.utmContent,
                utm_term: params.utmTerm,
                gclid: params.gclid,
                fbclid: params.fbclid
            }
        });

        if (params.emailType) {
            await this.logNative({
                firebaseName: 'email_link_open',
                params: {
                    email_type: params.emailType,
                    landing_path: params.path
                }
            });
        }
    }

    async syncUserProperties(user?: User | null): Promise<void> {
        const consentGranted = this.hasTrackingConsent();
        this.debug('syncUserProperties: called', {
            hasUser: !!user,
            consent: consentGranted,
            gender: user?.gender
        });
        await this.setUserProperty('marketing_consent', consentGranted ? 'granted' : 'denied');
        if (!consentGranted) {
            // await this.setUserProperty('gender_universe', null);
            // await this.setUserProperty('subscription_status', null);
            await this.setUserProperty('acq_source_last', null);
            await this.setUserProperty('acq_campaign_last', null);
            return;
        }

        // await this.setUserProperty('gender_universe', this.toGenderUniverse(user?.gender));
        // await this.setUserProperty('subscription_status', this.toSubscriptionStatus(user));
    }

    setConsentScope(scope?: User | string | null): string {
        this.currentConsentScope = this.resolveConsentScope(scope);
        this.debug('setConsentScope: updated', { scope: this.currentConsentScope });
        return this.currentConsentScope;
    }

    initializeTrackingConsent(defaultValue: boolean = false, scope?: User | string | null): boolean {
        const storageKey = this.getConsentStorageKey(scope);
        const current = localStorage.getItem(storageKey);
        if (current === '1' || current === '0') {
            return current === '1';
        }

        const granted = defaultValue === true;
        localStorage.setItem(storageKey, granted ? '1' : '0');
        this.debug('initializeTrackingConsent: initialized', { storageKey, granted });
        return granted;
    }

    setTrackingConsent(granted: boolean, scope?: User | string | null): void {
        const storageKey = this.getConsentStorageKey(scope);
        localStorage.setItem(storageKey, granted ? '1' : '0');
        this.debug('setTrackingConsent: updated', { storageKey, granted });
        void this.setAdvertiserTrackingEnabled(granted);
    }

    hasTrackingConsentChoice(scope?: User | string | null): boolean {
        const storageKey = this.getConsentStorageKey(scope);
        const value = localStorage.getItem(storageKey);
        return value === '1' || value === '0';
    }

    hasTrackingConsent(scope?: User | string | null): boolean {
        const storageKey = this.getConsentStorageKey(scope);
        return localStorage.getItem(storageKey) === '1';
    }

    getNetSubscriptionValue(type: 'geek' | 'branche' | 'vip' | 'vip_promo' | 'vip_pixel' | null | undefined): number {
        if (!type || (type !== 'geek' && type !== 'branche')) {
            return 0;
        }

        const gross = type === 'geek' ? SubscriptionPrices.GEEK : SubscriptionPrices.BRANCHE;
        return Number((gross / (1 + this.vatRate)).toFixed(2));
    }

    shouldTrackOnce(key: string): boolean {
        if (!key) {
            return true;
        }

        const storageKey = `tracking_event_${key}`;
        if (localStorage.getItem(storageKey)) {
            return false;
        }

        localStorage.setItem(storageKey, new Date().toISOString());
        return true;
    }

    private async logNative(options: TrackingLogOptions): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            this.debug('logNative: skipped (not native)', options);
            return;
        }

        this.debug('logNative: sending', {
            ...options,
            platform: Capacitor.getPlatform(),
            trackingPluginAvailable: Capacitor.isPluginAvailable('Tracking')
        });
        try {
            await Tracking.logEvent(options);
            this.debug('logNative: success', options);
        } catch (error) {
            this.warn('logNative: failed', error);
        }
    }

    private async setUserProperty(key: string, value: string | null): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            this.debug('setUserProperty: skipped (not native)', { key, value });
            return;
        }

        this.debug('setUserProperty: sending', {
            key,
            value,
            platform: Capacitor.getPlatform(),
            trackingPluginAvailable: Capacitor.isPluginAvailable('Tracking')
        });
        try {
            await Tracking.setUserProperty({ key, value });
            this.debug('setUserProperty: success', { key, value });
        } catch (error) {
            this.warn('setUserProperty: failed', { key, value, error });
        }
    }

    private async setAdvertiserTrackingEnabled(enabled: boolean): Promise<void> {
        if (!this.isIosNative()) {
            return;
        }

        this.debug('setAdvertiserTrackingEnabled: sending', {
            enabled,
            trackingPluginAvailable: Capacitor.isPluginAvailable('Tracking')
        });
        try {
            await Tracking.setAdvertiserTrackingEnabled({ enabled });
            this.debug('setAdvertiserTrackingEnabled: success', { enabled });
        } catch (error) {
            this.warn('setAdvertiserTrackingEnabled: failed', { enabled, error });
        }
    }

    async getTrackingAuthorizationStatus(): Promise<string> {
        if (!this.isIosNative()) {
            this.debug('getTrackingAuthorizationStatus: unavailable (not iOS native)');
            return 'unavailable';
        }
        if (!this.isAttPluginAvailable()) {
            this.warn('getTrackingAuthorizationStatus: ATT plugin unavailable', {
                ATT: Capacitor.isPluginAvailable('ATT'),
                ATTPlugin: Capacitor.isPluginAvailable('ATTPlugin')
            });
            return 'unavailable';
        }

        try {
            const result = await this.getAttStatus();
            this.debug('getTrackingAuthorizationStatus: success', result);
            return result?.status ?? 'unknown';
        } catch (error) {
            this.warn('getTrackingAuthorizationStatus: failed', error);
            return 'unknown';
        }
    }

    async requestTrackingAuthorization(): Promise<string> {
        if (!this.isIosNative()) {
            this.debug('requestTrackingAuthorization: unavailable (not iOS native)');
            return 'unavailable';
        }
        if (!this.isAttPluginAvailable()) {
            this.warn('requestTrackingAuthorization: ATT plugin unavailable', {
                ATT: Capacitor.isPluginAvailable('ATT'),
                ATTPlugin: Capacitor.isPluginAvailable('ATTPlugin')
            });
            return 'unavailable';
        }

        try {
            const result = await this.requestAttAuthorization();
            this.debug('requestTrackingAuthorization: success', result);
            return result?.status ?? 'unknown';
        } catch (error) {
            this.warn('requestTrackingAuthorization: failed', error);
            return 'unknown';
        }
    }

    async ensureAttAuthorizationForTracking(): Promise<boolean> {
        if (!this.isIosNative()) {
            return true;
        }

        const currentStatus = await this.getTrackingAuthorizationStatus();
        if (this.isAttStatusAllowed(currentStatus)) {
            this.debug('ensureAttAuthorizationForTracking: already allowed', { status: currentStatus });
            return true;
        }

        const requestedStatus = await this.requestTrackingAuthorization();
        const allowed = this.isAttStatusAllowed(requestedStatus);
        this.debug('ensureAttAuthorizationForTracking: after request', {
            before: currentStatus,
            after: requestedStatus,
            allowed
        });

        return allowed;
    }

    private isIosNative(): boolean {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
    }

    private isAttPluginAvailable(): boolean {
        return Capacitor.isPluginAvailable('ATT') || Capacitor.isPluginAvailable('ATTPlugin');
    }

    private isAttStatusAllowed(status: string): boolean {
        return status === 'authorized' || status === 'not_applicable' || status === 'unavailable';
    }

    private async getAttStatus(): Promise<{ status: string } | null> {
        try {
            this.debug('getAttStatus: using ATT plugin');
            return await ATT.getTrackingAuthorizationStatus();
        } catch (error) {
            this.warn('getAttStatus: ATT failed, trying ATTPlugin fallback', error);
            return ATTLegacy.getTrackingAuthorizationStatus();
        }
    }

    private async requestAttAuthorization(): Promise<{ status: string } | null> {
        try {
            this.debug('requestAttAuthorization: using ATT plugin');
            return await ATT.requestTrackingAuthorization();
        } catch (error) {
            this.warn('requestAttAuthorization: ATT failed, trying ATTPlugin fallback', error);
            return ATTLegacy.requestTrackingAuthorization();
        }
    }

    private async sendCapi(eventName: string, value?: number, currency?: string, eventId?: string): Promise<void> {
        if (!eventName) {
            this.warn('sendCapi: skipped (missing eventName)');
            return;
        }

        this.debug('sendCapi: sending', { eventName, value, currency, eventId });
        try {
            await this.http.post(`${environment.apiUrl}/tracking/meta`, {
                eventName,
                value,
                currency,
                eventId
            }).toPromise();
            this.debug('sendCapi: success', { eventName, eventId });
        } catch (error) {
            this.warn('sendCapi: failed', { eventName, eventId, error });
        }
    }

    private normalizePath(url: string): string {
        if (!url) {
            return '/';
        }

        const trimmed = url.split('?')[0].split('#')[0];
        if (!trimmed) {
            return '/';
        }

        return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    }

    private toGenderUniverse(gender?: string | null): string {
        if (gender === 'il') {
            return 'h';
        }
        if (gender === 'elle') {
            return 'f';
        }
        return 'other';
    }

    private toSubscriptionStatus(user?: User | null): string {
        const subscriptions = user?.subscriptions ?? [];
        const hasPaid = subscriptions.some((item) => (
            item.status === 'active'
            && item.type !== 'vip'
            && item.type !== 'vip_promo'
            && item.type !== 'vip_pixel'
        ));
        return hasPaid ? 'subscriber' : 'non_subscriber';
    }

    private mapScreenName(path: string): string {
        const staticMap: { [key: string]: string } = {
            '/': 'home_page',
            '/new': 'new_begaymers',
            '/communaute': 'salon_general',
            '/conversations': 'messagerie_liste',
            '/favoris': 'favoris',
            '/suggestions': 'consultation_suggestions',
            '/recherche': 'mode_recherche',
            '/nouvelle-recherche': 'faire_une_recherche',
            '/en-ligne': 'recherche_personne_en_ligne',
            '/widget_messages': 'widget',
            '/coups-de-coeur': 'coups_de_coeur',
            '/mes-notifications': 'notifications',
            '/presentation-abonnements': 'formules_abonnement',
            '/mon-abonnement': 'mon_abonnement',
            '/donnees-personnelles': 'donnees_personnelles',
            '/invite': 'inviter_un_ami',
            '/incognito': 'mode_incognito',
            '/mes-albums': 'mes_albums',
            '/mon-ideal': 'mon_ideal',
            '/a-propos-de-moi': 'a_propos_de_moi',
            '/ma-localisation': 'ma_localisation',
            '/visites': 'visites',
            '/recherches': 'recherches'
        };

        if (staticMap[path]) {
            return staticMap[path];
        }

        if (path.startsWith('/conversations/')) {
            return 'conversation_privee';
        }
        if (path.startsWith('/salon/')) {
            return 'conversation_salon_theme';
        }
        if (path.startsWith('/le-salon/')) {
            return 'conversation_salon_general';
        }
        if (path.startsWith('/profil/')) {
            return 'consulter_profil';
        }
        if (path.startsWith('/communaute/')) {
            return 'mes_salons_theme';
        }

        return path.replace(/\//g, '_').replace(/^_+/, '') || 'unknown_screen';
    }

    private getMetaScreenViewConfig(
        path: string,
        screenName: string
    ): { metaName: string; params: { [key: string]: any } } | null {
        const subscriptionScreens: { [key: string]: string } = {
            '/presentation-abonnements': 'subscription_offers',
            '/abonnement-geek': 'subscription_geek',
            '/abonnement-branche': 'subscription_branche',
            '/abonnement-validation': 'subscription_checkout',
            '/abonnement-confirmation': 'subscription_confirmation',
            '/abonnement-upgrade-confirmation': 'subscription_upgrade_confirmation',
            '/mon-abonnement': 'subscription_overview',
            '/gerer-mon-abonnement': 'subscription_management'
        };

        const contentId = subscriptionScreens[path];
        if (contentId) {
            return {
                metaName: 'fb_mobile_content_view',
                params: {
                    content_type: 'subscription',
                    content_id: contentId,
                    screen_name: screenName
                }
            };
        } else {
            return {
                metaName: 'fb_mobile_content_view',
                params: {
                    content_type: 'screen',
                    content_id: this.mapScreenName(path)
                }
            };
        }

        return null;
    }

    private extractTrackingParams(url: string): {
        path: string;
        emailType?: string;
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
        utmContent?: string;
        utmTerm?: string;
        gclid?: string;
        fbclid?: string;
    } | null {
        if (!url || url.indexOf('?') === -1) {
            return null;
        }

        const [rawPath, query] = url.split('?');
        const search = new URLSearchParams(query);
        const emailType = search.get('email_type') || undefined;
        const utmSource = search.get('utm_source') || undefined;
        const utmMedium = search.get('utm_medium') || undefined;
        const utmCampaign = search.get('utm_campaign') || undefined;
        const utmContent = search.get('utm_content') || undefined;
        const utmTerm = search.get('utm_term') || undefined;
        const gclid = search.get('gclid') || undefined;
        const fbclid = search.get('fbclid') || undefined;

        const hasTrackingParams = !!(emailType || utmSource || utmMedium || utmCampaign || utmContent || utmTerm || gclid || fbclid);
        if (!hasTrackingParams) {
            return null;
        }

        return {
            path: this.normalizePath(rawPath),
            emailType,
            utmSource,
            utmMedium,
            utmCampaign,
            utmContent,
            utmTerm,
            gclid,
            fbclid
        };
    }

    private getConsentStorageKey(scope?: User | string | null): string {
        const resolvedScope = this.resolveConsentScope(scope);
        return `${this.consentStoragePrefix}_${resolvedScope}`;
    }

    private resolveConsentScope(scope?: User | string | null): string {
        if (scope === undefined) {
            return this.currentConsentScope || this.anonymousConsentScope;
        }

        if (scope === null) {
            return this.anonymousConsentScope;
        }

        if (typeof scope === 'string') {
            const normalized = scope.trim();
            return normalized !== '' ? normalized : this.anonymousConsentScope;
        }

        const candidate = (scope as any)?.id || (scope as any)?.['@id'];
        if (typeof candidate === 'string' && candidate.trim() !== '') {
            return candidate.trim();
        }

        return this.anonymousConsentScope;
    }

    private debug(message: string, data?: any): void {
        if (!this.debugEnabled) {
            return;
        }

        if (data !== undefined) {
            console.log(this.debugPrefix, message, data);
            return;
        }

        console.log(this.debugPrefix, message);
    }

    private warn(message: string, data?: any): void {
        if (data !== undefined) {
            console.warn(this.debugPrefix, message, data);
            return;
        }

        console.warn(this.debugPrefix, message);
    }
}
